/**
 * Mathematical notation in labels.
 *
 * ## What this is, and what it deliberately is not
 *
 * It is **not** a LaTeX engine. A real one is a typesetting system — fractions, radicals,
 * matrices, delimiter stretching, font metrics for four families — and the smallest capable
 * implementation is larger than this entire application. Shipping a half-built one would be
 * worse than shipping none: a label that renders in the editor and silently degrades in an
 * export is a wrong worksheet.
 *
 * What it is: the notation a *geometry label* actually uses. Across a textbook's worth of
 * diagrams the labels are `A₁`, `x²`, `∠ABC`, `△ABC`, `α`, `AB ∥ CD`, `AB ⊥ CD`, `30°`. All
 * of those are a sequence of characters with a script level — nothing needs a two-dimensional
 * layout. So the grammar is exactly that, and it is written in LaTeX's spelling because that
 * is the spelling a maths teacher already knows.
 *
 * ```
 * \triangle ABC          →  △ABC
 * \angle A_1 B_2 C       →  ∠A₁B₂C
 * x^2 + y^2 = r^2        →  x²+y²=r²  (as runs; the painter raises them)
 * \alpha \approx 30\deg  →  α ≈ 30°
 * ```
 *
 * Anything outside the grammar is passed through unchanged rather than swallowed, so a label
 * is never silently lost. An unknown command shows as its own text — visible and fixable —
 * which is the behaviour a user can act on.
 *
 * ## Spacing is whatever the user typed
 *
 * In LaTeX a space after a command word is a delimiter and disappears, which is why
 * `	riangle ABC` sets as `△ABC`. Following that rule here was tried and abandoned: LaTeX
 * gets away with it because *math mode reinserts spacing by symbol class*, so the eaten
 * space around a relation comes back. Without that machinery, eating the space turns
 * `lpha + eta` into `α+ β` and `AB \parallel CD` into `AB ∥CD` — and the two cases
 * pull in opposite directions, so no single rule about spaces can serve both.
 *
 * Building the class table and the spacing engine would be building the typesetter this
 * module exists not to be. So spacing is left exactly as typed, which is the one rule that
 * never surprises anybody, and `{}` is available as an empty group for the rare label that
 * wants `	riangle{}ABC` set tight.
 */

/** Where a run sits relative to the baseline. */
export const ScriptLevel = {
  Base: 'base',
  Sub: 'sub',
  Super: 'super',
} as const;
export type ScriptLevel = (typeof ScriptLevel)[keyof typeof ScriptLevel];

export interface TextRun {
  readonly text: string;
  readonly level: ScriptLevel;
}

/**
 * Symbols a geometry label reaches for.
 *
 * Chosen from what appears in diagrams rather than from what LaTeX defines: the full symbol
 * table is thousands of entries, and a table nobody can scan is a table nobody uses. Adding
 * one is a single line here and needs no other change.
 */
export const SYMBOLS: Readonly<Record<string, string>> = Object.freeze({
  // Lower-case Greek, which is most of what a diagram labels angles with.
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  zeta: 'ζ',
  eta: 'η',
  theta: 'θ',
  iota: 'ι',
  kappa: 'κ',
  lambda: 'λ',
  mu: 'μ',
  nu: 'ν',
  xi: 'ξ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  phi: 'φ',
  chi: 'χ',
  psi: 'ψ',
  omega: 'ω',

  // Upper case, for the few that are not a Latin letter already.
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Xi: 'Ξ',
  Pi: 'Π',
  Sigma: 'Σ',
  Phi: 'Φ',
  Psi: 'Ψ',
  Omega: 'Ω',

  // Geometry proper.
  angle: '∠',
  measuredangle: '∡',
  triangle: '△',
  square: '□',
  perp: '⊥',
  parallel: '∥',
  nparallel: '∦',
  cong: '≅',
  sim: '∼',
  circ: '∘',
  deg: '°',
  prime: '′',

  // Relations and operators that turn up in a construction's caption.
  times: '×',
  cdot: '·',
  div: '÷',
  pm: '±',
  mp: '∓',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  approx: '≈',
  equiv: '≡',
  in: '∈',
  notin: '∉',
  subset: '⊂',
  cup: '∪',
  cap: '∩',
  rightarrow: '→',
  leftarrow: '←',
  leftrightarrow: '↔',
  mapsto: '↦',
  infty: '∞',
  therefore: '∴',
  because: '∵',
  ldots: '…',
  overline: '‾',
  vec: '⃗',
});

/**
 * Parses a label into runs.
 *
 * `_x` and `^x` take a single character; `_{...}` and `^{...}` take a group. That is the
 * LaTeX rule, and following it means a user who writes `A_10` gets `A₁0` — which looks
 * wrong until you realise it is what they asked for, and is what every other tool does.
 *
 * Adjacent runs at the same level are merged, so the painter measures `ABC` once rather
 * than three times.
 */
export function parseMarkup(content: string): readonly TextRun[] {
  const runs: TextRun[] = [];
  let buffer = '';
  const level: ScriptLevel = ScriptLevel.Base;

  /** Appends a run, merging into the previous one when they share a level. */
  const emit = (text: string, at: ScriptLevel): void => {
    if (text === '') return;
    const last = runs[runs.length - 1];
    if (last?.level === at) {
      runs[runs.length - 1] = { text: last.text + text, level: at };
    } else {
      runs.push({ text, level: at });
    }
  };

  const flush = (): void => {
    emit(buffer, level);
    buffer = '';
  };

  let index = 0;

  while (index < content.length) {
    const character = content[index];
    if (character === undefined) break;

    if (character === '\\') {
      const command = readCommand(content, index + 1);
      if (command === null) {
        // A trailing backslash, or `\\` — emit one and move on rather than dropping it.
        buffer += '\\';
        index += content[index + 1] === '\\' ? 2 : 1;
        continue;
      }
      const symbol = SYMBOLS[command.name];
      // An unknown command survives as its own text: visible and fixable beats vanished.
      buffer += symbol ?? `\\${command.name}`;
      index = command.next;
      continue;
    }

    /*
     * Braces outside a script are grouping and produce nothing, so `	riangle{}ABC` reads
     * as `△ABC`. This is the escape hatch for the one thing spacing cannot express — see
     * the note on spacing above.
     */
    if (character === '{' || character === '}') {
      index += 1;
      continue;
    }

    if (character === '_' || character === '^') {
      const wanted = character === '_' ? ScriptLevel.Sub : ScriptLevel.Super;
      const group = readGroup(content, index + 1);
      if (group === null) {
        buffer += character;
        index += 1;
        continue;
      }

      flush();
      for (const run of parseMarkup(group.text)) {
        // A script inside a script stays at the outer script's level: two-dimensional
        // nesting is exactly the thing this grammar does not do. Emitted through `emit`,
        // so flattening `x^{a^b}` leaves one run rather than two adjacent ones.
        emit(run.text, wanted);
      }
      index = group.next;
      continue;
    }

    buffer += character;
    index += 1;
  }

  flush();
  return runs;
}

/** The plain characters of a label, with markup resolved. Used for measuring and for search. */
export function plainText(content: string): string {
  return parseMarkup(content)
    .map((run) => run.text)
    .join('');
}

/** True when the label contains anything this module would transform. */
export function hasMarkup(content: string): boolean {
  return /[\\_^]/.test(content);
}

/* -------------------------------------------------------------------- scanning */

function readCommand(content: string, from: number): { name: string; next: number } | null {
  let index = from;
  while (index < content.length && /[A-Za-z]/.test(content[index] ?? '')) index += 1;
  if (index === from) return null;
  return { name: content.slice(from, index), next: index };
}

function readGroup(content: string, from: number): { text: string; next: number } | null {
  const opener = content[from];
  if (opener === undefined) return null;

  if (opener !== '{') {
    // A bare `_x`: one character, which is the LaTeX rule.
    return { text: opener, next: from + 1 };
  }

  let depth = 1;
  let index = from + 1;
  while (index < content.length && depth > 0) {
    const character = content[index];
    if (character === '{') depth += 1;
    else if (character === '}') depth -= 1;
    index += 1;
  }

  // An unclosed group takes the rest of the label. Discarding it would lose the user's
  // text over a missing brace.
  const end = depth === 0 ? index - 1 : content.length;
  return { text: content.slice(from + 1, end), next: depth === 0 ? index : content.length };
}
